import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DevHubTextField,
  DevHubFormControl,
  DevHubAutocomplete,
} from '@devxdiscover/devhub-ui';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';

/**
 * The schema for the {@link ArtifactoryProjectPicker} return value.
 */
export const ArtifactoryProjectPickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'string',
    title: 'Artifactory Project Key',
    description: 'An existing Artifactory project key',
  },
};

type ProjectOption = {
  displayName: string;
  projectKey: string;
};

/**
 * A scaffolder field extension that provides an autocomplete picker
 * for Artifactory projects. Fetches the full list from the DevHub
 * backend REST API and filters client-side.
 * @param props The field extension props provided by the scaffolder
 * @returns The selected project key (string) or undefined.
 */
export const ArtifactoryProjectPicker = (
  props: FieldExtensionComponentProps<string | undefined>,
) => {
  const { formData, onChange, rawErrors, idSchema, required } = props;
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const { token } = await identityApi.getCredentials();
        const response = await fetch(
          `${backendUrl}/api/det-portal/artifactory/projects`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const fetched: ProjectOption[] = await response.json();
        if (!cancelled) {
          const sorted = [...fetched].sort((a, b) =>
            a.displayName.localeCompare(b.displayName),
          );
          setProjects(sorted);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [backendUrl, identityApi]);

  const selectedProject = useMemo(() => {
    if (!formData || projects.length === 0) {
      return null;
    }

    return projects.find(project => project.projectKey === formData) ?? null;
  }, [formData, projects]);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return projects;
    const q = inputValue.toLowerCase();
    return projects.filter(
      p =>
        p.displayName.toLowerCase().includes(q) ||
        p.projectKey.toLowerCase().includes(q),
    );
  }, [projects, inputValue]);

  const handleSelect = useCallback(
    (_: unknown, value: ProjectOption | null) => {
      onChange(value?.projectKey);
    },
    [onChange],
  );

  const getOptionLabel = (option: ProjectOption) =>
    `${option.displayName} (${option.projectKey})`;

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors && rawErrors.length > 0 && !formData}
    >
      {error ? (
        <DevHubTextField
          error
          label="Error"
          defaultValue="Failed to load projects"
          helperText="Could not fetch Artifactory projects."
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          id={idSchema?.$id}
          value={selectedProject}
          loading={loading}
          loadingText="Loading projects..."
          options={filteredOptions}
          getOptionLabel={getOptionLabel}
          onChange={handleSelect}
          onInputChange={(_, val) => setInputValue(val)}
          autoSelect={false}
          renderInput={params => (
            <DevHubTextField
              {...params}
              label="Existing Project"
              margin="normal"
              helperText="Search by project name or key"
              variant="outlined"
              required={required}
              InputProps={params.InputProps}
            />
          )}
        />
      )}
    </DevHubFormControl>
  );
};
